import { getRun, startRange } from './categoryRun';
import { getListing, runListing } from './listingStore';
import { hasUnpickedConflicts } from './infoPicks';
import { assemblyStepsFrom, categoryBandsFrom } from './categoryBands';
import { extractParts, getParts } from './partsRun';
import { extractSteps, getSubSteps } from './stepsRun';
import { findHardwareInteractions } from './hardwareInteractionsRun';
import { getStepParts, getStepText, runStepPartsPhase } from './stepPartsRun';
import { runDimensionsIfUseful } from './dimensionsRun';
import { runPageCrops } from './hardwareCropsRun';
import { renderPartCrops } from './partCrop';
import { RUN_MODEL, STEPS_MODEL } from './models';
import { haltGate } from './runHalt';
import { newRun, readState, writeState } from './docState';

const MATERIAL = 'Matériel fourni';

// Pause and Stop used to be two buttons and two mechanisms. Pause let the phase on
// screen finish and kept a resume point; Stop aborted the phase mid-call and threw
// the point away. The distinction did not survive one question: what happens to a
// Gemini call already sent?
//
// Nothing. Aborting the request cancels it in the browser; the Flask route runs to
// the end and the call is billed either way. So Stop's abort never saved money — it
// only guaranteed that an answer already paid for was thrown away. Pause kept it.
// One side was strictly worse, and the remaining difference (whether the run could
// be continued) is free to keep.
//
// So there is one button, and one mechanism: stop issuing new calls, keep everything
// already bought, leave a resume point. The halt itself lives in runHalt, shared by
// every phase; here we only need to notice it happened and record where to pick up.

// Where a run stopped short, as the index of the stage to pick up at. Its presence
// IS "this run can be resumed"; a completed run removes it.
//
// Shared with everything else about the document, unlike the halt epoch in runHalt.
// That is a request aimed at a run in flight RIGHT NOW, in one browser, and no run
// outlives its tab. A resume point is a fact about RESULTS, and results now outlive
// the tab AND the machine: a run one admin paused is one the other can pick up.
//
// This was a Map plus localStorage, which is precisely what docState is, so it is
// neither now.
const resumeKey = (docId: string) => `notice.resumeAt.${docId}`;

function writeResumePoint(docId: string, stage: number | null) {
  writeState(docId, resumeKey(docId), stage);
}

// The stage a paused (or conflict-halted) run would pick up at, or null when there is
// nothing to resume.
export function resumePoint(docId: string): number | null {
  const n = readState<number | null>(docId, resumeKey(docId), null);
  // Stage 0 is a real resume point — the point names the phase that was HALTED, and
  // Phase 1 is where a halt most often lands — so it cannot double as "nothing to
  // resume". Anything that is not a whole stage index is not one.
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : null;
}

// How a run ended, for the caller to report:
//   'done'      — the chain ran to its last phase;
//   'paused'    — the user hit Pause; new calls stopped, whatever was in flight
//                 landed, and the rest waits behind `resumePoint`;
//   'needsPick' — the manual and the web listing disagree, and only the user can
//                 settle it (see below). Also resumable: the phases before it did
//                 their work, and redoing them to answer one question is waste.
export type RunAllOutcome = 'done' | 'paused' | 'needsPick';

// What each stage is handed. Everything a stage needs arrives through here, so the stage
// list can live at module scope — which is what lets the numbers below be READ off that
// list instead of kept in sync with it by hand.
type StageCtx = {
  docId: string;
  file: Blob;
  numPages: number;
  t: (k: string) => string;
  resume: boolean;
};

// The pipeline, in order — stage 0 is the reader's Phase 1 (page classification + the
// brand / listing reads, in parallel), stage 1 is Phase 2 (the supplied parts, their sizes
// and their materials), stages 2 and 3 are Phase 4 (the steps' overview, then the per-step
// analysis), stage 4 is Phase 5 (its hardware interactions) and the last is Phase 2 again (the manual
// crops). Phase
// 3, the materials, is not a stage: it is read by hand.
//
// THE NUMBERS FOLLOW THE DEPENDENCIES NOW. The analysis is handed the repartition — it is
// context in the prompt of every step call — so the repartition cannot follow it, and for
// a while the reader showed them the other way round because that was the order you READ
// them in. Two orders, only one of them arguable; and a phase number that does not predict
// which phase waits on which is a number doing no work. So they were renumbered rather
// than the calls reordered.
//
// The last stage is the one thing still out of line: it fills a Phase 2 row, and asks about
// the tools the step analysis adds to the parts list.
//
// The photographs and the reconstructions are still run BY HAND, part by part, from the
// views beside the parts list: each picture is a separate paid choice rather than a step
// the chain can take on its own.
// A STAGE IS NOT A PHASE, and saying so is the whole of this shape.
//
// It was one per phase, and the button's label counted the list: "phases 1 to N" with N as
// the number of stages plus the manual. That held exactly as long as the two were the same
// thing, and stopped the day a second stage landed inside one phase — the manual crops,
// which are more work on the parts table rather than a page of their own. The label went on
// counting stages and promised a phase 5 nobody could open.
//
// So each stage says which phase it belongs to and the label takes the highest. A stage is
// a unit of work a Pause can land between; a phase is a page of the reader. Two different
// facts, and the moment they stopped agreeing the derived one had to say which it was
// derived from.
type Stage = {
  // As the READER numbers them — the manual itself is Phase 1, so the classification is 2.
  // The phase this stage belongs to, as the reader numbers them. IN ORDER now — the
  // repartition is Phase 3 and the analysis, which reads it, is Phase 4. It used to be the
  // other way round on screen while running in this order, so the number predicted nothing;
  // the phases were renumbered to match the dependencies rather than the calls reordered,
  // since the dependency is real and the reading order was a preference.
  //
  // Two stages still carry Phase 2: the parts list near the front, and the manual crops at
  // the very back — they fill a Phase 2 row, and ask about the tools the analysis adds.
  phase: number;
  run: (ctx: StageCtx) => Promise<RunAllOutcome | void>;
};

const STAGES: Stage[] = [
  { phase: 1, run: async ({ docId, file, numPages, t, resume }) => {
    await Promise.all([
      startRange(docId, file, 1, numPages, t, resume),
      // One read per submodel link — a no-op unless Phase 1 was given a listing URL.
      runListing(docId, t, resume),
    ]);
    // The manual and the listing can name a different brand or product. Nothing here is
    // entitled to guess which is right, so the chain hands the question back rather than
    // run on a value the user never agreed to.
    if (hasUnpickedConflicts(docId)) return 'needsPick';
  } },

  { phase: 2, run: async ({ docId, file, t, resume }) => {
    const segments = getRun(docId).segments;
    const bands = categoryBandsFrom(segments, MATERIAL);
    if (bands.model && bands.pages.length) {
      // Phase 2's images are no longer part of the run — they are generated (or searched)
      // by hand from that phase, part by part.
      await extractParts(docId, file, bands, t, resume);
    }
    // How big each part really is, off the listing's dimension drawing. It belongs to
    // this stage and to no other: it reads the list the call above just produced, and the
    // drawing it reads FROM was found by the stage before this one. Between those two it
    // is the only place it can go.
    //
    // Silent when there is nothing to do — no drawing in the strip, or every part already
    // sized — because most manuals have neither, and a run is not the place to be told so.
    await runDimensionsIfUseful(docId, getListing(docId).productName, RUN_MODEL);

    // WHAT EACH PART IS MADE OF IS NOT ASKED HERE ANY MORE. It was the last call of this stage,
    // answering for the materials phase — the only phase that reads it — so it is that phase's reading now,
    // which judges the materials and assigns the parts in one answer (see replacePartMaterials).
    //
    // THE LISTING CROPS ARE NOT RUN HERE EITHER: one call per photograph in series, then a trace
    // per crop they found — the longest wait in the app, and a row with its own button.
  } },

  // Phase 4: share the supplied parts out among the steps. (Phase 3, the materials, is not
  // in the run: it is read by hand, per colourway, and nothing in this chain reads it.)
  //
  // Not skipped on a resume, unlike the extraction: it is ONE call over the whole manual,
  // so there is no half of it to keep, and re-asking it is the only way to have it at all
  // if the halt landed here.
  { phase: 4, run: async ({ docId, file, t }) => {
    const { steps } = assemblyStepsFrom(getRun(docId).segments);
    // BOTH CALLS, from the same function the panel's button presses — the repartition, then
    // what each step prints. The order and the reason for it live there.
    await runStepPartsPhase(docId, file, steps, getParts(docId), t);
  } },

  // Phase 4, its second half: analyse every step — what it joins, what it achieves, and its breakdown into
  // sub-steps, one call per step, the steps walked in order (the pieces are numbered through
  // the whole manual, so each call continues the one before it). It reads the repartition
  // above.
  //
  // WHAT THE CHAIN STILL DOES NOT DO is anything from the other views of phase 2. The
  // photographs and the reconstructions are paid, per part, and deliberate; they were left
  // out when they were phases of their own and being drawn on the same page as the parts
  // list is not a reason to sweep them in.
  { phase: 4, run: async ({ docId, file, t, resume }) => {
    const { steps } = assemblyStepsFrom(getRun(docId).segments);
    const items = getParts(docId);
    if (!steps.length || !items.length) return;
    await extractSteps(
      docId, file, steps, items, getStepParts(docId), getStepText(docId), STEPS_MODEL, t, resume,
    );
  } },

  // Phase 5's hardware interactions: which pieces of hardware work together, read off the sub-steps
  // the stage above just wrote — so after it, and its own stage, so a run stopped between the
  // two resumes here rather than re-analysing every step. Over the whole manual in one call.
  { phase: 5, run: async ({ docId, file, t }) => {
    const { steps } = assemblyStepsFrom(getRun(docId).segments);
    const items = getParts(docId);
    if (!steps.length || !items.length) return;
    await findHardwareInteractions(docId, file, steps, getSubSteps(docId), items, STEPS_MODEL, t);
  } },

  // Where every part is drawn in the steps — the manual search in Phase 2's listing-crops row.
  //
  // LAST, though it needs neither reading of the steps: it asks about every part in the list,
  // and the step analysis above is what adds the tools the carton does not hold. A screwdriver
  // listed after this ran would have no drawings, and its drawings are what size it.
  //
  // Still phase 2: what it fills is the parts table's listing-crops row.
  { phase: 2, run: async ({ docId, file }) => {
    const { steps } = assemblyStepsFrom(getRun(docId).segments);
    const items = getParts(docId);
    if (!steps.length || !items.length) return;
    await runPageCrops(docId, file, steps, items, await renderPartCrops(file, items));
  } },
];

// How many stages the run-all button executes — the progress counter's "/N", so it is
// always exactly what the button runs. By construction: add or remove a stage above and
// both this and the counter follow, with no magic number to update.
export const RUN_ALL_PHASE_COUNT = STAGES.length;

// THE LAST PHASE IT REACHES, as the reader numbers them — which is what the button's label
// says, and is not the same number. Taken from the stages rather than written down, so a
// stage added inside an existing phase moves neither and a stage that opens a new one moves
// this alone.
export const RUN_ALL_LAST_PHASE = Math.max(...STAGES.map((s) => s.phase));

// AND WHICH PHASES, since "up to the last" is not the same thing: the materials (Phase 3) sit
// between two phases the run reaches and are never run by it. What the reader hatches as coming.
export const RUN_ALL_PHASES: ReadonlySet<number> = new Set(STAGES.map((s) => s.phase));

// Run the reader steps that are automated, in dependency order, as a list of stages
// so a run can stop between them and be picked up later. Each stage awaits its own work:
//   0. page classification (+ the general-info and listing reads, in parallel) — Phase 1;
//   1. supplied-parts extraction, then their dimensions and their materials — Phase 2;
//   2. the repartition of those parts among the assembly steps — Phase 3;
//   3. the per-step analysis and its sub-steps — Phase 4;
//   4. every other drawing of each part, found in the steps that use it — Phase 2 again.
//
// So it runs 2, 3, 4, 5 in that order, and then goes back for the one Phase 2 answer that
// could not be had until 4 and 5 were in: the drawings are hunted per step, and hunting
// without knowing that step's parts and tools is what labels a dowel end "wood button".
//
// WHAT IT NEVER RUNS is the rest of Phase 2: the photographs and the 3D reconstructions.
// Those are per part and paid, done by hand from their own Generate / Search buttons —
// each picture is a separate choice. The listing crops are the same, for a different
// reason: they are the longest wait in the app and had their own button given to them.
// Phases 5 and 6 make no calls at all.
//
// `from` is the stage to pick up at, or null for a fresh run.
//
// Null and not 0, because 0 is a resume of the FIRST stage and those are opposites:
// a fresh run clears every phase, a resume of stage 0 keeps the pages Phase 1 had
// already classified. Since the resume point now names the phase that was halted
// rather than the one after it, stage 0 is the commonest point there is — Phase 1 is
// where a Pause usually lands. Conflating the two would make resuming mean "start
// over", which is the one thing it must never mean.
export async function runAllSteps(
  docId: string,
  file: Blob,
  numPages: number,
  t: (k: string) => string,
  from: number | null = null,
): Promise<RunAllOutcome> {
  if (numPages < 1) return 'done';

  // Every stage is told it is resuming, not just the one being picked up: a stage
  // AFTER the resume point has no results to keep (the fresh run that preceded it
  // cleared them, and it never ran), so for those "resume" and "fresh" do the same
  // thing. One flag, no special cases.
  const resume = from !== null;
  const start = from ?? 0;
  // A fresh pipeline is a new version of the document, so it gets its own run: the
  // last one stays browsable from Phase 1 instead of being overwritten, and two admins
  // starting at once write to two runs instead of one of them losing.
  //
  // A resume does NOT: it is the same run, continuing. Opening one would copy its own
  // half-finished answers forward and then resume into the copy, which is a version of
  // nothing anybody did.
  // The new run is forked server-side WITHOUT the downstream phases (parts / images /
  // steps): newRun drops them, so from the instant the pipeline starts each of Phase 2/3/4
  // shows "running" or "Waiting on previous steps" — never the last run's stale result.
  // A resume keeps the same run (and its finished stages), which is the whole point of it.
  if (!resume) await newRun(docId);
  // The halt epoch as of now. A halt asked for during the PREVIOUS run cannot leak
  // into this one — that is the whole reason it is a counter and not a flag.
  const halted = haltGate(docId);
  writeResumePoint(docId, null);

  const ctx: StageCtx = { docId, file, numPages, t, resume };
  for (let i = start; i < STAGES.length; i++) {
    const outcome = await STAGES[i].run(ctx);

    // Paused: this stage stopped issuing calls partway, so it is INCOMPLETE and the
    // run picks up at IT, not at the one after. Resuming re-asks only for the items
    // it never reached — that is what makes coming back here cost nothing, and what
    // stops Phase 2 from ever reading a half-classified manual (the bug that killed
    // the old per-phase Stop).
    if (halted()) {
      writeResumePoint(docId, i);
      return 'paused';
    }

    // needsPick is the opposite case: the stage finished its work and is asking a
    // question, so the run picks up AFTER it.
    if (outcome === 'needsPick') {
      writeResumePoint(docId, i + 1);
      return 'needsPick';
    }
  }
  writeResumePoint(docId, null);
  return 'done';
}

// Continue a run that stopped short, at the stage it never got to. A no-op when
// there is nothing to resume.
export function resumeAllSteps(
  docId: string,
  file: Blob,
  numPages: number,
  t: (k: string) => string,
): Promise<RunAllOutcome> {
  const from = resumePoint(docId);
  return from === null
    ? Promise.resolve<RunAllOutcome>('done')
    : runAllSteps(docId, file, numPages, t, from);
}
